import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { CheckCircle2 } from "lucide-react";
import { clearCart } from "@/lib/cart";
import { track } from "@/lib/tracking";
import { getCheckoutSession } from "@/lib/payments.functions";
import { recordPurchaseEvent } from "@/lib/analytics-qa.functions";
import { getStripeEnvironment } from "@/lib/stripe";

export const Route = createFileRoute("/checkout/return")({
  head: () => ({
    meta: [
      { title: "Thanks for your order — Glow Long Golf" },
      { name: "description", content: "Order confirmation for Glow Long Golf. Your glow-in-the-dark golf balls are on the way — check your email for tracking details." },
    ],
  }),
  validateSearch: (s: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  }),
  component: ReturnPage,
});

function ReturnPage() {
  const { session_id } = Route.useSearch();
  useEffect(() => {
    if (!session_id) return;
    clearCart();
    let cancelled = false;
    (async () => {
      try {
        const s = await getCheckoutSession({
          data: { sessionId: session_id, environment: getStripeEnvironment() },
        });
        if (cancelled || (s as any).error) {
          track("purchase", { transaction_id: session_id });
          return;
        }
        const data = s as Exclude<typeof s, { error: string }>;
        const items = data.line_items.map((li, idx) => ({
          item_id: `line_${idx}`,
          item_name: li.description,
          quantity: li.quantity,
          price: li.quantity ? li.amount_total / li.quantity / 100 : 0,
        }));
        track("purchase", {
          transaction_id: session_id,
          currency: (data.currency || "usd").toUpperCase(),
          value: (data.amount_total || 0) / 100,
          items,
        });
        // Mirror the GA fire into our admin Analytics QA report.
        recordPurchaseEvent({
          data: {
            transactionId: session_id,
            valueCents: data.amount_total || 0,
            currency: data.currency || "usd",
            itemsCount: items.length,
            pagePath: typeof window !== "undefined" ? window.location.pathname : undefined,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          },
        }).catch(() => { /* QA-only; never block the thank-you page */ });
      } catch {
        track("purchase", { transaction_id: session_id });
      }
    })();
    return () => { cancelled = true; };
  }, [session_id]);

  return (
    <section className="mx-auto max-w-xl px-4 py-24 text-center sm:px-6 lg:px-8">
      <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
      <h1 className="mt-4 font-display text-3xl font-semibold">Thanks for your order!</h1>
      <p className="mt-3 text-muted-foreground">
        We'll send a receipt to your email shortly. Your Glow Long pack ships from Amazon's fulfillment network and you'll get tracking once it's on its way.
      </p>
      <Link to="/" className="mt-8 inline-flex rounded-full border border-border bg-card px-6 py-3 text-sm font-medium hover:bg-secondary">
        Back to home
      </Link>
    </section>
  );
}
